import React from "react";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider, useAuth } from "./lib/auth";
import { RbacProvider } from "./lib/rbac";
import RequirePermission from "./components/RequirePermission";
import Login from "./pages/Login";
import CustomerLogin from "./pages/CustomerLogin";
import AuthCallback from "./pages/AuthCallback";
import AdminLayout from "./components/AdminLayout";
import CustomerLayout from "./components/CustomerLayout";
import Dashboard from "./pages/admin/Dashboard";
import Customers from "./pages/admin/Customers";
import CustomerDetail from "./pages/admin/CustomerDetail";
import Cards from "./pages/admin/Cards";
import Fleet from "./pages/admin/Fleet";
import Transactions from "./pages/admin/Transactions";
import PriceLists from "./pages/admin/PriceLists";
import Invoices from "./pages/admin/Invoices";
import InvoiceDetail from "./pages/admin/InvoiceDetail";
import Reports from "./pages/admin/Reports";
import Dealers from "./pages/admin/Dealers";
import PricingGroups from "./pages/admin/PricingGroups";
import Margins from "./pages/admin/Margins";
import StationOverridesReport from "./pages/admin/StationOverridesReport";
import PetroPassUsMarginManager from "./pages/admin/PetroPassUsMarginManager";
import PetroPassCanadaMarginManager from "./pages/admin/PetroPassCanadaMarginManager";
import FuelTaxRates from "./pages/admin/FuelTaxRates";
import SalesTools from "./pages/admin/SalesTools";
import SalesToolsHub from "./pages/admin/SalesToolsHub";
import ProspectsList from "./pages/admin/ProspectsList";
import ProspectDetail from "./pages/admin/ProspectDetail";
import PdfExtractions from "./pages/admin/PdfExtractions";
import ResumableImports from "./pages/admin/ResumableImports";
import AdminJobs from "./pages/admin/AdminJobs";
import AdminAudit from "./pages/admin/AdminAudit";
import AdminAuditReconciliation from "./pages/admin/AdminAuditReconciliation";
import TeamAccess from "./pages/admin/TeamAccess";
import AdminProfitability from "./pages/admin/AdminProfitability";
import DeletedRecords from "./pages/admin/DeletedRecords";
import Users from "./pages/admin/Users";
import CustomerDashboard from "./pages/customer/CustomerDashboard";
import CustomerCards from "./pages/customer/CustomerCards";
import CustomerTransactions from "./pages/customer/CustomerTransactions";
import CustomerPriceLists from "./pages/customer/CustomerPriceLists";
import CustomerProfile from "./pages/customer/CustomerProfile";
import CustomerChangeRequests from "./pages/customer/CustomerChangeRequests";
import CustomerSupportTickets from "./pages/customer/CustomerSupportTickets";
import CustomerSupportTicketDetail from "./pages/customer/CustomerSupportTicketDetail";
import CustomerUserAccess from "./pages/customer/CustomerUserAccess";
// Phase-4 P2 (2026-09-11) — Customer invoice tab enablement.
import CustomerInvoices from "./pages/customer/CustomerInvoices";
import CustomerInvoiceDetail from "./pages/customer/CustomerInvoiceDetail";
import CustomerActivate from "./pages/CustomerActivate";
import CustomerForgotPassword from "./pages/CustomerForgotPassword";
import CustomerResetPassword from "./pages/CustomerResetPassword";
import CustomerSecurity from "./pages/customer/CustomerSecurity";
import AdminCardChangeRequests from "./pages/admin/AdminCardChangeRequests";
import AdminSupportTickets from "./pages/admin/AdminSupportTickets";
import AdminSupportTicketDetail from "./pages/admin/AdminSupportTicketDetail";
import PortalAlerts from "./pages/admin/PortalAlerts";
import PortalHealth from "./pages/admin/PortalHealth";
import InvitationAccept from "./pages/InvitationAccept";

function Protected({ children, role }) {
  const { user, loading } = useAuth();
  const location = useLocation();
  if (loading)
    return (
      <div className="h-screen flex items-center justify-center">
        <div className="font-mono text-xs tracking-widest text-zinc-500">LOADING…</div>
      </div>
    );
  if (!user) {
    // Route unauthenticated visitors to the correct login realm based
    // on where they were trying to reach. Anyone hitting `/portal/*`
    // gets sent to /customer/login; everyone else to the team login.
    const to = location.pathname.startsWith("/portal") ? "/customer/login" : "/login";
    return <Navigate to={to} state={{ from: location }} replace />;
  }
  // Treat staff users (RBAC Phase 1) equivalent to admin for admin-realm gating.
  const isAdminRealm =
    user.role === "admin" || (user.user_type || "").toLowerCase() === "staff";
  if (role === "admin" && !isAdminRealm) {
    return <Navigate to="/portal" replace />;
  }
  return children;
}

// Shorthand — wraps a page element with an RBAC guard for the given
// permission codes. anyOf semantics.
const G = (anyOf, element) =>
  anyOf && anyOf.length > 0
    ? <RequirePermission anyOf={anyOf}>{element}</RequirePermission>
    : element;

function AppRouter() {
  const location = useLocation();
  if (location.hash?.includes("session_id=")) return <AuthCallback />;
  return (
    <Routes>
      <Route path="/" element={<Login />} />
      <Route path="/login" element={<Login />} />
      <Route path="/customer/login" element={<CustomerLogin />} />
      <Route path="/auth/callback" element={<AuthCallback />} />
      <Route path="/invite/accept" element={<InvitationAccept />} />
      <Route path="/portal/activate" element={<CustomerActivate />} />
      <Route path="/customer/forgot-password" element={<CustomerForgotPassword />} />
      <Route path="/customer/reset-password" element={<CustomerResetPassword />} />

      {/* ADMIN */}
      <Route
        path="/"
        element={
          <Protected role="admin">
            <AdminLayout />
          </Protected>
        }
      >
        <Route path="dashboard"                    element={G(["dashboard.view"],              <Dashboard />)} />
        <Route path="customers"                    element={G(["customers.view"],              <Customers />)} />
        <Route path="customers/:customerId"        element={G(["customers.view"],              <CustomerDetail />)} />
        <Route path="cards"                        element={G(["cards.view"],                  <Cards />)} />
        <Route path="card-change-requests"         element={G(["card_change_requests.approve","cards.view"], <AdminCardChangeRequests />)} />
        <Route path="support-tickets"              element={G(["support_tickets.view"],        <AdminSupportTickets />)} />
        <Route path="support-tickets/:ticketId"    element={G(["support_tickets.view"],        <AdminSupportTicketDetail />)} />
        <Route path="fleet"                        element={<Fleet />} />
        <Route path="transactions"                 element={G(["transactions.view"],           <Transactions />)} />
        <Route path="price-lists"                  element={G(["pricing.view"],                <PriceLists />)} />
        <Route path="invoices"                     element={G(["accounts_collectable.view"],   <Invoices />)} />
        <Route path="invoices/:invoiceId"          element={G(["accounts_collectable.view"],   <InvoiceDetail />)} />
        <Route path="reports"                      element={G(["financial_reports.download"],  <Reports />)} />
        <Route path="dealers"                      element={<Dealers />} />
        <Route path="pricing-groups"               element={G(["pricing.view"],                <PricingGroups />)} />
        <Route path="margins"                      element={G(["margins.view"],                <Margins />)} />
        <Route path="margins/station-overrides"    element={G(["margins.view"],                <StationOverridesReport />)} />
        <Route path="margins/petro-pass-us"        element={G(["margins.view"],                <PetroPassUsMarginManager />)} />
        <Route path="margins/petro-pass-ca"        element={G(["margins.view"],                <PetroPassCanadaMarginManager />)} />
        <Route path="fuel-tax-rates"               element={G(["system_settings.manage"],      <FuelTaxRates />)} />
        <Route path="sales-tools"                  element={<SalesToolsHub />} />
        <Route path="sales-tools/analyzer"         element={<SalesTools />} />
        <Route path="sales-tools/crm"              element={<ProspectsList />} />
        <Route path="pdf-extractions"              element={G(["transactions.upload"],         <PdfExtractions />)} />
        <Route path="resumable-imports"            element={G(["transactions.upload"],         <ResumableImports />)} />
        <Route path="jobs"                         element={G(["system_settings.manage"],      <AdminJobs />)} />
        <Route path="admin-audit"                  element={G(["audit_log.view"],              <AdminAudit />)} />
        <Route path="portal-alerts"                 element={G(["audit_log.view"],              <PortalAlerts />)} />
        <Route path="portal-health"                 element={G(["audit_log.view"],              <PortalHealth />)} />
        <Route path="audit-reconciliation"         element={G(["profitability.view"],          <AdminAuditReconciliation />)} />
        <Route path="team-access"                  element={G(["users.view","roles.view"],     <TeamAccess />)} />
        <Route path="profitability"                element={G(["profitability.view"],          <AdminProfitability />)} />
        <Route path="deleted-records"              element={G(["transactions.restore"],        <DeletedRecords />)} />
        <Route path="sales-tools/crm/:prospectId"  element={<ProspectDetail />} />
        <Route path="users"                        element={G(["users.view"],                  <Users />)} />
      </Route>

      {/* CUSTOMER */}
      <Route
        path="/portal"
        element={
          <Protected>
            <CustomerLayout />
          </Protected>
        }
      >
        <Route index element={<CustomerDashboard />} />
        {/* Phase-4 P2 (2026-09-11) — Customer invoice tab re-enabled.
           Backend `/api/invoices*` GETs are now permitted for
           customer-typed sessions via the narrow
           `_CUSTOMER_DUAL_MODE_GET_ALLOWLIST` entries in
           rbac_middleware.py. Handler ownership predicates
           (invoices.py L504-505 / L561 / L594) enforce tenant
           isolation. Write verbs remain staff-only. */}
        <Route path="invoices" element={<CustomerInvoices />} />
        <Route path="invoices/:invoiceId" element={<CustomerInvoiceDetail />} />
        <Route path="cards" element={<CustomerCards />} />
        <Route path="change-requests" element={<CustomerChangeRequests />} />
        <Route path="support" element={<CustomerSupportTickets />} />
        <Route path="support/:ticketId" element={<CustomerSupportTicketDetail />} />
        <Route path="transactions" element={<CustomerTransactions />} />
        <Route path="price-lists" element={<CustomerPriceLists />} />
        <Route path="user-access" element={<CustomerUserAccess />} />
        <Route path="security" element={<CustomerSecurity />} />
        <Route path="profile" element={<CustomerProfile />} />
      </Route>

      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

export default function App() {
  return (
    <AuthProvider>
      <RbacProvider>
        <BrowserRouter>
          <AppRouter />
          <Toaster position="top-right" richColors closeButton />
        </BrowserRouter>
      </RbacProvider>
    </AuthProvider>
  );
}
